export const buster = () => String(Math.random()).substring(2);

export const isString = (x: unknown): x is String =>
  !!x && typeof x === 'string';

export const isArray = (x: unknown): x is [] =>
  Array.isArray
    ? Array.isArray(x)
    : Object.prototype.toString.call(x) === '[object Array]';

export const isObject = (x: unknown): x is object =>
  !!x && typeof x === 'object' && !isArray(x);

export const isFunction = (x: unknown): x is Function =>
  !!x && typeof x === 'function';

/**
 * In config options a particular value may be specifying the height of something, either directly via a static value or by
 * reference to an element (whose height should be used). This function is a convenience wrapper to retrieve it or use a default
 * if no value found.
 * If val is null or undefined then dfault is returned. If val is a CSS selector string then return the clientHeight of the
 * matching element, or dfault if an error occurs (eg. invalid selector). Otherwise treat as a static value and return val.
 * @param {String | *} val
 * @param {*} defaultValue
 * @returns {DOMElement | *}
 */
export const getHeight = (
  val: string | number | unknown,
  defaultValue: number,
) => {
  try {
    if (val === undefined || val === null) {
      return defaultValue;
    }
    if (isString(val)) {
      return document.querySelector(val as string)?.clientHeight;
    }
    return val;
  } catch (x) {
    return defaultValue;
  }
};

/**
 * If val is undefined or null then return the default value, which itself may also be undefined and that's ok.
 * Alternatively, if val isn't undefined or null then convert it to truthy.
 * @param {Any} val
 * @param {Any} defaultValue
 */
export const toBoolean = (val?: unknown, defaultValue?: boolean) =>
  val === undefined || val === null ? defaultValue : !!val;

/**
 * For all properties of the macros object, Search and replace all matches of {{property}}
 * in template with the value from macros.
 */
export const processMacros = <T extends Record<string, unknown>>(
  macros: T,
  template: string,
) =>
  template.replace(/{{([A-Za-z]\w*)}}/g, (_, tag) => {
    if (Object.prototype.hasOwnProperty.call(macros, tag)) {
      return encodeURIComponent((macros[tag] as string) ?? '');
    }
    return '';
  });

/**
 * Wait until a function returns truthy, or we run out of time.
 * @param {Function} testFn Function that returns truthy when desired condition met
 * @param {Function} doneFn Function called with testFn result when done (falsey if not done)
 * @param {Number} maxTime Max time in millis to try (default 1000)
 * @param {Number} interval Millis between testFn calls (default 100)
 */
export const waitUntil = <T extends unknown>(
  testFn: () => T,
  doneFn: (res: T) => void,
  maxTime?: number,
  interval?: number,
) => {
  const start = Date.now();
  var timerId = 0;
  const clear = () => {
    if (timerId) {
      window.clearInterval(timerId);
      timerId = 0;
    }
  };
  timerId = window.setInterval(() => {
    if (timerId) {
      const res = testFn();
      if (res || Date.now() - start >= (maxTime || 1000)) {
        clear();
        doneFn(res);
      }
    }
  }, interval || 100);
  return clear;
};

/**
 * A while loop that safely exits if it takes too long
 * As a third party running on a publisher page, we must ensure that we don't
 * accidentally enter an infinite blocking loop that breaks a publisher page.
 */
export const safeWhile = (
  testFn: () => boolean,
  bodyFn: (breakFn: () => void) => void,
  elapsedFn: (elapsedTime: number) => void = () => {},
  maxTime: number = 3000,
) => {
  const start = Date.now();
  let shouldBreak = false;
  const breakFn = () => {
    shouldBreak = true;
  };
  while (testFn() && !shouldBreak) {
    const elapsed = Date.now() - start;
    if (elapsed >= maxTime) {
      elapsedFn(elapsed);
      break;
    }
    bodyFn(breakFn);
  }
};
